import { useState, useEffect } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import {
  Truck,
  Globe,
  Warehouse,
  Package,
  Ship,
  Plane,
  FileCheck,
  Building,
  Mail,
  Phone,
  MapPin,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Shield,
  Clock,
  Award,
  MessageCircle,
  Search,
  User,
  UserPlus,
  Wrench,
  Users
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_889fd383-3aba-477f-a361-c6065bd808b3/artifacts/y0ruu11n_logo.png";

// New Address Announcement Popup
const NewAddressPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { language } = useLanguage();

  useEffect(() => {
    // Check if user has already seen the popup
    const hasSeenPopup = localStorage.getItem('greymar_new_address_seen');
    if (!hasSeenPopup) {
      // Show popup after a short delay
      const timer = setTimeout(() => setIsOpen(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem('greymar_new_address_seen', 'true');
  };

  const content = {
    en: {
      title: '📍 New Location!',
      subtitle: 'We have moved to a new address',
      address: '7703 NW 46th St, Doral, FL 33166',
      description: 'Visit us at our new facilities with more space to serve you better.',
      button: 'Got it!'
    },
    es: {
      title: '📍 ¡Nueva Ubicación!',
      subtitle: 'Nos hemos mudado a una nueva dirección',
      address: '7703 NW 46th St, Doral, FL 33166',
      description: 'Visítanos en nuestras nuevas instalaciones con más espacio para servirte mejor.',
      button: '¡Entendido!'
    }
  };

  const t = content[language] || content.en;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[99999]"
          />
          
          {/* Popup */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100000] w-[90%] max-w-md"
          >
            <div className="bg-[var(--background-paper)] border-2 border-[var(--primary)] rounded-lg overflow-hidden shadow-2xl">
              {/* Header with animated border */}
              <div className="relative bg-gradient-to-r from-[var(--primary)] to-red-700 p-6 text-center">
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  className="text-4xl mb-2"
                >
                  🏢
                </motion.div>
                <h2 className="text-2xl md:text-3xl font-bold text-white">{t.title}</h2>
                <p className="text-white/90 mt-1">{t.subtitle}</p>
              </div>
              
              {/* Content */}
              <div className="p-6 text-center">
                <div className="flex items-center justify-center gap-3 mb-4">
                  <MapPin className="text-[var(--primary)] flex-shrink-0" size={24} />
                  <span className="text-xl font-semibold text-white">{t.address}</span>
                </div>
                <p className="text-[var(--text-secondary)] mb-6">{t.description}</p>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleClose}
                  className="w-full py-3 bg-[var(--primary)] text-white font-bold rounded-lg hover:bg-red-700 transition-colors"
                >
                  {t.button}
                </motion.button>
              </div>
              
              {/* Close button */}
              <button
                onClick={handleClose}
                className="absolute top-3 right-3 text-white/80 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Hook to hide Emergent badge
const useHideEmergentBadge = () => {
  useEffect(() => {
    const hideBadge = () => {
      // Remove any element containing "Emergent" or "Made with"
      document.querySelectorAll('body > div, body > a, body > iframe').forEach(el => {
        if (el.id === 'root' || el.classList.contains('App')) return;
        
        const text = el.textContent || el.innerText || '';
        const html = el.innerHTML || '';
        
        if (text.includes('Emergent') || text.includes('Made with') || 
            html.includes('Emergent') || html.includes('Made with') ||
            el.id?.includes('emergent') || el.className?.includes('emergent')) {
          el.remove();
        }
      });

      // Remove fixed positioned elements at bottom (except our WhatsApp)
      document.querySelectorAll('div, a, iframe').forEach(el => {
        const style = window.getComputedStyle(el);
        if (style.position === 'fixed' && 
            parseInt(style.bottom) < 100 && 
            parseInt(style.right) < 200 &&
            !el.classList.contains('whatsapp-btn') &&
            el.closest('.whatsapp-btn') === null) {
          const text = el.textContent || '';
          if (text.includes('Emergent') || text.includes('Made with')) {
            el.remove();
          }
        }
      });

      // Check for iframes
      document.querySelectorAll('iframe').forEach(iframe => {
        if (iframe.src?.includes('emergent')) {
          iframe.remove();
        }
      });
    };

    // Run multiple times to catch dynamically injected elements
    hideBadge();
    const intervals = [100, 300, 500, 1000, 2000, 3000, 5000];
    const timeouts = intervals.map(delay => setTimeout(hideBadge, delay));

    // Continuous observer
    const observer = new MutationObserver(() => {
      hideBadge();
    });
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true 
    });

    // Also run on window load
    window.addEventListener('load', hideBadge);

    return () => {
      timeouts.forEach(clearTimeout);
      observer.disconnect();
      window.removeEventListener('load', hideBadge);
    };
  }, []);
};

// Navigation Component
const Navigation = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#home", label: t.nav.home },
    { href: "#about", label: t.nav.about },
    { href: "#services", label: t.nav.services },
    { href: "#locations", label: t.nav.locations },
    { href: "#contact", label: t.nav.contact },
  ];

  const toolsLinks = [
    { 
      href: "https://www.grupotechnoservers.com/reg/registro.asp?mynumber=35", 
      label: t.nav.registerLocker,
      icon: UserPlus,
      external: true
    },
    { 
      href: "https://apptecnokargo.us", 
      label: t.nav.loginLocker,
      icon: User,
      external: true
    },
    { 
      href: "https://www.grupotechnoservers.com/tracking/ras_paq.asp?mynumber=35#no-back-button", 
      label: t.nav.tracking,
      icon: Search,
      external: true
    },
    { 
      href: "https://www.grupotechnoservers.com/tracking/registro_tracking.asp?mynumber=35#no-back-button", 
      label: t.nav.registerTracking,
      icon: FileCheck,
      external: true
    },
    { 
      href: "https://www.grupotechnoservers.com/default.asp?mynumber=35#no-back-button", 
      label: t.nav.sessionMatriz,
      icon: Building,
      external: true
    },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass border-b border-[var(--border)]" : "bg-transparent"
      }`}
      data-testid="navigation"
    >
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <a href="#home" className="flex items-center" data-testid="logo-link">
          <img src={LOGO_URL} alt="Greymar Logistics" className="h-10 md:h-12" />
        </a>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="nav-link text-sm uppercase tracking-wider text-[var(--text-secondary)] hover:text-white transition-colors"
              data-testid={`nav-${link.href.slice(1)}`}
            >
              {link.label}
            </a>
          ))}
          
          {/* Tools Dropdown */}
          <div className="relative" onMouseLeave={() => setToolsMenuOpen(false)}>
            <button
              onMouseEnter={() => setToolsMenuOpen(true)}
              onClick={() => setToolsMenuOpen(!toolsMenuOpen)}
              className="nav-link text-sm uppercase tracking-wider text-[var(--text-secondary)] hover:text-white transition-colors flex items-center gap-1"
              data-testid="nav-tools-dropdown"
            >
              {t.nav.tools}
              <ChevronDown size={14} className={`transition-transform ${toolsMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            
            <AnimatePresence>
              {toolsMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 mt-2 w-56 bg-[var(--background-paper)] border border-[var(--border)] shadow-xl"
                  onMouseEnter={() => setToolsMenuOpen(true)}
                >
                  {toolsLinks.map((tool) => {
                    const Icon = tool.icon;
                    return (
                      <a
                        key={tool.href}
                        href={tool.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-[var(--text-secondary)] hover:text-white hover:bg-[var(--background-subtle)] transition-colors border-b border-[var(--border)] last:border-b-0"
                        data-testid={`tool-${tool.label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <Icon size={18} className="text-[var(--primary)]" />
                        {tool.label}
                      </a>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <button
            onClick={toggleLanguage}
            className="text-sm uppercase tracking-wider px-3 py-1 border border-[var(--border)] hover:border-[var(--primary)] transition-colors"
            data-testid="language-toggle"
          >
            {language === "en" ? "ES" : "EN"}
          </button>
          <a
            href="#contact"
            className="btn-primary px-6 py-2 text-sm uppercase tracking-wider font-medium"
            data-testid="nav-quote-btn"
          >
            {t.nav.quote}
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden text-white p-2"
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-[var(--background-paper)] border-t border-[var(--border)]"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-sm uppercase tracking-wider py-2"
                >
                  {link.label}
                </a>
              ))}
              
              {/* Mobile Tools Section */}
              <div className="border-t border-[var(--border)] pt-4 mt-2">
                <div className="flex items-center gap-2 text-[var(--primary)] text-sm uppercase tracking-wider mb-3">
                  <Wrench size={16} />
                  {t.nav.tools}
                </div>
                {toolsLinks.map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <a
                      key={tool.href}
                      href={tool.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 py-2 text-sm text-[var(--text-secondary)]"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Icon size={16} />
                      {tool.label}
                    </a>
                  );
                })}
              </div>
              
              <div className="flex items-center gap-4 pt-4 border-t border-[var(--border)]">
                <button
                  onClick={toggleLanguage}
                  className="text-sm uppercase px-3 py-1 border border-[var(--border)]"
                >
                  {language === "en" ? "ES" : "EN"}
                </button>
                <a
                  href="#contact"
                  className="btn-primary px-6 py-2 text-sm uppercase"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t.nav.quote}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Hero Section with Explosive Carousel
const HeroSection = () => {
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      image: "/team-miami/miami_0.jpg",
      alt: "Greymar Logistics Team"
    },
    {
      image: "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?q=80&w=2070",
      alt: "Shipping port with containers"
    },
    {
      image: "/team-miami/hero_extra.jpg",
      alt: "Greymar Warehouse Operations"
    },
    {
      image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=2070",
      alt: "Cargo ship at sea"
    },
    {
      image: "https://images.unsplash.com/photo-1578575437130-527eed3abbec?q=80&w=2070",
      alt: "Warehouse logistics"
    },
    {
      image: "https://images.unsplash.com/photo-1553413077-190dd305871c?q=80&w=2070",
      alt: "Truck transportation"
    },
    {
      image: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=2070",
      alt: "Cargo airplane"
    },
    {
      image: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?q=80&w=2070",
      alt: "Global logistics network"
    },
    {
      image: "/team-miami/hero_last.jpg",
      alt: "Greymar Miami Facility"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000); // Change every 4 seconds
    return () => clearInterval(timer);
  }, [slides.length]);

  const slideVariants = {
    enter: {
      scale: 1.2,
      opacity: 0,
    },
    center: {
      scale: 1,
      opacity: 1,
    },
    exit: {
      scale: 0.8,
      opacity: 0,
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Carousel Background */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ 
            duration: 1.2, 
            ease: [0.43, 0.13, 0.23, 0.96]
          }}
          className="absolute inset-0"
        >
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('${slides[currentSlide].image}')`,
            }}
          />
          {/* Glitch/Flash effect on transition */}
          <motion.div
            initial={{ opacity: 0.8 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 bg-[var(--primary)]"
          />
        </motion.div>
      </AnimatePresence>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-[var(--background)]" />
      
      {/* Animated lines/grid effect */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(var(--primary) 1px, transparent 1px), linear-gradient(90deg, var(--primary) 1px, transparent 1px)`,
          backgroundSize: '100px 100px'
        }} />
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-1 transition-all duration-500 ${
              index === currentSlide 
                ? 'w-12 bg-[var(--primary)]' 
                : 'w-4 bg-white/30 hover:bg-white/50'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <motion.div 
            className="flex justify-center mb-8"
            animate={{ 
              scale: [1, 1.02, 1],
            }}
            transition={{ 
              duration: 3, 
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <img src={LOGO_URL} alt="Greymar" className="h-16 md:h-24 drop-shadow-2xl" />
          </motion.div>
          
          <motion.h1 
            className="text-4xl md:text-6xl lg:text-7xl font-extrabold mb-6 tracking-tight"
            key={`title-${currentSlide}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            {t.hero.tagline}
          </motion.h1>
          
          <motion.p 
            className="text-lg md:text-xl text-[var(--text-secondary)] max-w-3xl mx-auto mb-10"
            key={`subtitle-${currentSlide}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            {t.hero.subtitle}
          </motion.p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.a
              href="#contact"
              className="btn-primary px-8 py-4 text-lg uppercase tracking-wider font-semibold flex items-center gap-2"
              data-testid="hero-cta"
              whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(255,0,0,0.5)" }}
              whileTap={{ scale: 0.95 }}
            >
              {t.hero.cta}
              <ChevronRight size={20} />
            </motion.a>
            <motion.a
              href="#services"
              className="px-8 py-4 text-lg uppercase tracking-wider font-semibold border border-white/30 hover:border-white transition-colors"
              data-testid="hero-secondary-cta"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {t.hero.secondary}
            </motion.a>
          </div>
        </motion.div>

        {/* Certifications */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="mt-16 flex flex-wrap justify-center gap-4"
        >
          {["TSA CERTIFIED", "NVOCC LICENSED", "IPOSTEL AUTHORIZED"].map((cert, i) => (
            <motion.span 
              key={i} 
              className="cert-badge"
              whileHover={{ scale: 1.1, borderColor: "var(--primary)" }}
            >
              <Shield size={14} className="text-[var(--primary)]" />
              {cert}
            </motion.span>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-white rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const { t } = useLanguage();

  const stats = [
    { value: "20+", label: t.about.stats.years },
    { value: "28", label: t.about.stats.countries },
    { value: "10K+", label: t.about.stats.shipments },
    { value: "5K+", label: t.about.stats.clients },
  ];

  return (
    <section id="about" className="py-24 bg-[var(--background-paper)]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[var(--primary)] text-sm uppercase tracking-widest">
              {t.about.title}
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold mt-4 mb-6">
              {t.about.subtitle}
            </h2>
            <div className="section-divider" />
            <p className="text-[var(--text-secondary)] text-lg leading-relaxed mb-8">
              {t.about.description}
            </p>
            
            <div className="bg-[var(--background-subtle)] p-6 border-l-4 border-[var(--primary)]">
              <h3 className="font-semibold text-xl mb-2">{t.about.mission}</h3>
              <p className="text-[var(--text-secondary)]">{t.about.missionText}</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-2 gap-6"
          >
            {stats.map((stat, index) => (
              <div
                key={index}
                className="bg-[var(--background)] p-8 border border-[var(--border)] red-glow-border text-center"
              >
                <div className="stat-number">{stat.value}</div>
                <div className="text-[var(--text-secondary)] mt-2 text-sm uppercase tracking-wider">
                  {stat.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const { t } = useLanguage();

  const services = [
    { key: "ground", icon: Truck },
    { key: "international", icon: Globe },
    { key: "warehousing", icon: Warehouse },
    { key: "consolidation", icon: Package },
    { key: "nvocc", icon: Ship },
    { key: "tsa", icon: Plane },
    { key: "customs", icon: FileCheck },
    { key: "bonded", icon: Building },
    { key: "ipostel", icon: Mail },
  ];

  return (
    <section id="services" className="py-24 bg-[var(--background)]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[var(--primary)] text-sm uppercase tracking-widest">
            {t.services.title}
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold mt-4">{t.services.subtitle}</h2>
          <div className="section-divider mx-auto mt-6" />
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            const data = t.services.items[service.key];
            
            return (
              <motion.div
                key={service.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="service-card bg-[var(--background-paper)] p-8 border border-[var(--border)] card-glow group"
                data-testid={`service-${service.key}`}
              >
                <div className="service-icon mb-6">
                  <Icon size={24} className="text-[var(--text-secondary)] group-hover:text-[var(--primary)] transition-colors" />
                </div>
                <h3 className="text-xl font-bold mb-1">{data.title}</h3>
                <span className="text-[var(--primary)] text-sm uppercase tracking-wider">{data.subtitle}</span>
                <p className="text-[var(--text-secondary)] mt-4 text-sm leading-relaxed">
                  {data.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseSection = () => {
  const { t } = useLanguage();

  const features = [
    { key: "experience", icon: Clock },
    { key: "fleet", icon: Truck },
    { key: "global", icon: Globe },
    { key: "certified", icon: Award },
  ];

  return (
    <section className="py-24 bg-[var(--background-paper)] relative overflow-hidden" data-testid="why-section">
      {/* Background pattern */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1684610529682-553625a1ffed?q=80&w=2070')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-extrabold">{t.why.title}</h2>
          <div className="section-divider mx-auto mt-6" />
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const data = t.why.items[feature.key];
            
            return (
              <motion.div
                key={feature.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 border border-[var(--primary)] flex items-center justify-center">
                  <Icon size={28} className="text-[var(--primary)]" />
                </div>
                <h3 className="text-lg font-bold mb-2">{data.title}</h3>
                <p className="text-[var(--text-secondary)] text-sm">{data.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Locations Section
const LocationsSection = () => {
  const { t } = useLanguage();

  return (
    <section id="locations" className="py-24 bg-[var(--background)]" data-testid="locations-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[var(--primary)] text-sm uppercase tracking-widest">
            {t.locations.title}
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold mt-4">{t.locations.subtitle}</h2>
          <div className="section-divider mx-auto mt-6" />
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Miami */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="location-card bg-[var(--background-paper)] p-8 border border-[var(--border)]"
            data-testid="location-miami"
          >
            <h3 className="text-2xl font-bold mb-6">{t.locations.miami.title}</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <MapPin size={20} className="text-[var(--primary)] mt-1 flex-shrink-0" />
                <span className="text-[var(--text-secondary)]">{t.locations.miami.address}</span>
              </div>
              <div className="flex items-center gap-4">
                <Phone size={20} className="text-[var(--primary)] flex-shrink-0" />
                <a href="tel:+17866550745" className="text-[var(--text-secondary)] hover:text-white transition-colors">
                  {t.locations.miami.phone}
                </a>
              </div>
              <div className="flex items-center gap-4">
                <Mail size={20} className="text-[var(--primary)] flex-shrink-0" />
                <a href="mailto:info@greymarlogistics.com" className="text-[var(--text-secondary)] hover:text-white transition-colors">
                  info@greymarlogistics.com
                </a>
              </div>
            </div>
          </motion.div>

          {/* Orlando */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="location-card bg-[var(--background-paper)] p-8 border border-[var(--border)]"
            data-testid="location-orlando"
          >
            <h3 className="text-2xl font-bold mb-6">{t.locations.orlando.title}</h3>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <MapPin size={20} className="text-[var(--primary)] mt-1 flex-shrink-0" />
                <span className="text-[var(--text-secondary)]">{t.locations.orlando.address}</span>
              </div>
              <div className="flex items-center gap-4">
                <Phone size={20} className="text-[var(--primary)] flex-shrink-0" />
                <a href="tel:+13214195100" className="text-[var(--text-secondary)] hover:text-white transition-colors">
                  {t.locations.orlando.phone}
                </a>
              </div>
              <div className="flex items-center gap-4">
                <Mail size={20} className="text-[var(--primary)] flex-shrink-0" />
                <a href="mailto:info@greymarlogistics.com" className="text-[var(--text-secondary)] hover:text-white transition-colors">
                  info@greymarlogistics.com
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Team Section with Infinite Marquee Carousel
const TeamSection = () => {
  const { t } = useLanguage();

  // Miami team members with real photos
  const miamiTeam = [
    { id: 1, name: "TEAM MIAMI", role: "", image: "/team-miami/miami_0.jpg" },
    { id: 2, name: "TEAM MIAMI", role: "", image: "/team-miami/miami_1.jpg" },
    { id: 3, name: "TEAM MIAMI", role: "", image: "/team-miami/miami_2.jpg" },
    { id: 4, name: "TEAM MIAMI", role: "", image: "/team-miami/miami_3.jpg" },
  ];

  const venezuelaTeam = [
    { id: 1, name: "TEAM VZLA", role: "", image: "/team-venezuela/venezuela_1.jpeg" },
    { id: 2, name: "TEAM VZLA", role: "", image: "/team-venezuela/venezuela_2.jpeg" },
    { id: 3, name: "TEAM VZLA", role: "", image: "/team-venezuela/venezuela_3.jpeg" },
    { id: 4, name: "TEAM VZLA", role: "", image: "/team-venezuela/venezuela_4.jpeg" },
    { id: 5, name: "TEAM VZLA", role: "", image: "/team-venezuela/venezuela_5.jpeg" },
    { id: 6, name: "TEAM VZLA", role: "", image: "/team-venezuela/venezuela_6.jpeg" },
    { id: 7, name: "TEAM VZLA", role: "", image: "/team-venezuela/venezuela_7.jpeg" },
    { id: 8, name: "TEAM VZLA", role: "", image: "/team-venezuela/venezuela_8.jpeg" },
    { id: 9, name: "TEAM VZLA", role: "", image: "/team-venezuela/venezuela_9.jpeg" },
  ];

  const TeamMemberCard = ({ member }) => (
    <div className="flex-shrink-0 w-40 md:w-48 text-center group mx-4">
      <div className="relative w-32 h-32 md:w-40 md:h-40 mx-auto mb-4 overflow-hidden border-2 border-[var(--border)] group-hover:border-[var(--primary)] transition-colors">
        {member.image ? (
          <img 
            src={member.image} 
            alt={member.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-[var(--background-subtle)] flex items-center justify-center">
            <User size={48} className="text-[var(--text-muted)]" />
          </div>
        )}
      </div>
      <h4 className="font-semibold text-white text-sm md:text-base">{member.name}</h4>
      <p className="text-xs md:text-sm text-[var(--text-secondary)]">{member.role}</p>
    </div>
  );

  // Infinite Marquee Component
  const InfiniteMarquee = ({ team, direction = "left", speed = "fast" }) => {
    const speedClass = speed === "fast" ? "animate-marquee-fast" : "animate-marquee";
    const reverseClass = direction === "right" ? "animate-marquee-reverse-fast" : "";
    
    return (
      <div className="overflow-hidden relative">
        {/* Gradient overlays for smooth fade effect */}
        <div className="absolute left-0 top-0 bottom-0 w-20 md:w-32 bg-gradient-to-r from-[var(--background-paper)] to-transparent z-10 pointer-events-none" />
        <div className="absolute right-0 top-0 bottom-0 w-20 md:w-32 bg-gradient-to-l from-[var(--background-paper)] to-transparent z-10 pointer-events-none" />
        
        <div className={`flex ${direction === "right" ? reverseClass : speedClass}`}>
          {/* First set */}
          {team.map((member) => (
            <TeamMemberCard key={`set1-${member.id}`} member={member} />
          ))}
          {/* Duplicate for seamless loop */}
          {team.map((member) => (
            <TeamMemberCard key={`set2-${member.id}`} member={member} />
          ))}
          {/* Triple for extra smoothness */}
          {team.map((member) => (
            <TeamMemberCard key={`set3-${member.id}`} member={member} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <section id="team" className="py-24 bg-[var(--background-paper)] overflow-hidden" data-testid="team-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[var(--primary)] text-sm uppercase tracking-widest">
            {t.team.title}
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold mt-4">{t.team.subtitle}</h2>
          <div className="section-divider mx-auto mt-6" />
        </motion.div>
      </div>

      {/* Miami Team - Scrolls Left */}
      <div className="mb-12">
        <div className="max-w-7xl mx-auto px-6 mb-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex items-center gap-4"
          >
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center border border-[var(--primary)] bg-[var(--primary)]/10">
              <Users size={20} className="text-[var(--primary)]" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold">{t.team.miami}</h3>
          </motion.div>
        </div>
        <InfiniteMarquee team={miamiTeam} direction="left" speed="fast" />
      </div>

      {/* Venezuela Team - Scrolls Right (opposite direction) */}
      <div>
        <div className="max-w-7xl mx-auto px-6 mb-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex items-center gap-4"
          >
            <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center border border-[var(--primary)] bg-[var(--primary)]/10">
              <Users size={20} className="text-[var(--primary)]" />
            </div>
            <h3 className="text-xl md:text-2xl font-bold">{t.team.venezuela}</h3>
          </motion.div>
        </div>
        <InfiniteMarquee team={venezuelaTeam} direction="right" speed="fast" />
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-[var(--text-muted)] mt-12 text-sm"
        >
          {t.team.placeholder}
        </motion.p>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setSubmitted(true);
    setFormData({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    
    setTimeout(() => setSubmitted(false), 5000);
  };

  const services = [
    "Ground Transportation",
    "International Logistics",
    "Warehousing",
    "Cargo Consolidation",
    "NVOCC Services",
    "Air Cargo (TSA)",
    "Customs Brokerage",
    "Bonded Warehouse",
    "Courier Services"
  ];

  return (
    <section id="contact" className="py-24 bg-[var(--background-paper)]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-[var(--primary)] text-sm uppercase tracking-widest">
            {t.contact.title}
          </span>
          <h2 className="text-4xl md:text-5xl font-extrabold mt-4">{t.contact.subtitle}</h2>
          <div className="section-divider mx-auto mt-6" />
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-xl font-bold mb-6">{t.contact.info.title}</h3>
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center border border-[var(--border)]">
                  <Mail size={20} className="text-[var(--primary)]" />
                </div>
                <div>
                  <p className="text-sm text-[var(--text-muted)] uppercase tracking-wider">Email</p>
                  <a href="mailto:info@greymarlogistics.com" className="hover:text-[var(--primary)] transition-colors">
                    {t.contact.info.email}
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center border border-[var(--border)]">
                  <Phone size={20} className="text-[var(--primary)]" />
                </div>
                <div>
                  <p className="text-sm text-[var(--text-muted)] uppercase tracking-wider">Miami</p>
                  <a href="tel:+17866550745" className="hover:text-[var(--primary)] transition-colors">
                    +1 (786) 655-0745
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center border border-[var(--border)]">
                  <Clock size={20} className="text-[var(--primary)]" />
                </div>
                <div>
                  <p className="text-sm text-[var(--text-muted)] uppercase tracking-wider">Hours</p>
                  <span>{t.contact.info.hours}</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubmit}
            className="lg:col-span-2 space-y-6"
            data-testid="contact-form"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm uppercase tracking-wider mb-2 text-[var(--text-secondary)]">
                  {t.contact.form.name} *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="form-input"
                  data-testid="input-name"
                />
              </div>
              <div>
                <label className="block text-sm uppercase tracking-wider mb-2 text-[var(--text-secondary)]">
                  {t.contact.form.email} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="form-input"
                  data-testid="input-email"
                />
              </div>
              <div>
                <label className="block text-sm uppercase tracking-wider mb-2 text-[var(--text-secondary)]">
                  {t.contact.form.phone}
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="input-phone"
                />
              </div>
              <div>
                <label className="block text-sm uppercase tracking-wider mb-2 text-[var(--text-secondary)]">
                  {t.contact.form.company}
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="form-input"
                  data-testid="input-company"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm uppercase tracking-wider mb-2 text-[var(--text-secondary)]">
                {t.contact.form.service}
              </label>
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                className="form-input cursor-pointer"
                data-testid="select-service"
              >
                <option value="">{t.contact.form.selectService}</option>
                {services.map((service) => (
                  <option key={service} value={service}>{service}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm uppercase tracking-wider mb-2 text-[var(--text-secondary)]">
                {t.contact.form.message}
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={5}
                placeholder={t.contact.form.messagePlaceholder}
                className="form-input resize-none"
                data-testid="textarea-message"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full py-4 text-lg uppercase tracking-wider font-semibold disabled:opacity-50"
              data-testid="submit-btn"
            >
              {isSubmitting ? t.contact.form.sending : t.contact.form.submit}
            </button>

            {submitted && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center text-green-500 py-4"
              >
                Message sent successfully! We'll get back to you soon.
              </motion.div>
            )}
          </motion.form>
        </div>
      </div>
    </section>
  );
};

// Facility Showcase Section
const FacilityShowcase = () => {
  const { language } = useLanguage();
  
  return (
    <section className="py-20 bg-[var(--background)] overflow-hidden" data-testid="facility-section">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.8, rotateY: -15 }}
          whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ 
            duration: 0.8, 
            ease: [0.43, 0.13, 0.23, 0.96]
          }}
          className="relative"
        >
          {/* Decorative frame corners */}
          <div className="absolute -top-4 -left-4 w-20 h-20 border-t-4 border-l-4 border-[var(--primary)]" />
          <div className="absolute -top-4 -right-4 w-20 h-20 border-t-4 border-r-4 border-[var(--primary)]" />
          <div className="absolute -bottom-4 -left-4 w-20 h-20 border-b-4 border-l-4 border-[var(--primary)]" />
          <div className="absolute -bottom-4 -right-4 w-20 h-20 border-b-4 border-r-4 border-[var(--primary)]" />
          
          {/* Glowing border effect */}
          <motion.div
            className="absolute inset-0 border-2 border-[var(--primary)] opacity-50"
            animate={{
              boxShadow: [
                "0 0 20px rgba(255, 0, 0, 0.3)",
                "0 0 40px rgba(255, 0, 0, 0.5)",
                "0 0 20px rgba(255, 0, 0, 0.3)"
              ]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          
          {/* Main image */}
          <motion.img
            src="/facility_miami.jpg"
            alt="Greymar Miami Facility"
            className="w-full h-auto relative z-10"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          />
          
          {/* Caption overlay */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-8 z-20"
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white">
              {language === 'es' ? 'Nuestras Instalaciones en Miami' : 'Our Miami Facility'}
            </h3>
            <p className="text-[var(--text-secondary)] mt-2">
              {language === 'es' 
                ? 'Centro de operaciones estratégicamente ubicado para servir a toda América' 
                : 'Strategically located operations center serving all of the Americas'}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[var(--background)] border-t border-[var(--border)] py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <img src={LOGO_URL} alt="Greymar" className="h-12 mb-6" />
            <p className="text-[var(--text-secondary)] mb-6 max-w-md">
              {t.footer.description}
            </p>
            <div className="flex gap-4">
              {["TSA", "NVOCC", "IPOSTEL"].map((cert) => (
                <span key={cert} className="text-xs px-3 py-1 border border-[var(--border)] text-[var(--text-muted)]">
                  {cert}
                </span>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className="font-bold mb-6 text-sm uppercase tracking-wider">{t.footer.quickLinks}</h4>
            <ul className="space-y-3">
              {[
                { href: "#home", label: t.nav.home },
                { href: "#about", label: t.nav.about },
                { href: "#services", label: t.nav.services },
                { href: "#contact", label: t.nav.contact },
              ].map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-[var(--text-secondary)] hover:text-white transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          
          <div>
            <h4 className="font-bold mb-6 text-sm uppercase tracking-wider">{t.footer.contact}</h4>
            <ul className="space-y-3 text-[var(--text-secondary)]">
              <li>
                <a href="mailto:info@greymarlogistics.com" className="hover:text-white transition-colors">
                  info@greymarlogistics.com
                </a>
              </li>
              <li>
                <a href="tel:+17866550745" className="hover:text-white transition-colors">
                  +1 (786) 655-0745
                </a>
              </li>
              <li>7703 NW 46th St, Doral, FL 33166</li>
            </ul>
          </div>
        </div>
        
        <div className="pt-8 border-t border-[var(--border)] flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[var(--text-muted)] text-sm">
            © {currentYear} Greymar International Freight LLC. {t.footer.rights}
          </p>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Button
const WhatsAppButton = () => {
  return (
    <a
      href="https://wa.me/17866550745"
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-btn"
      data-testid="whatsapp-btn"
      aria-label="Contact via WhatsApp"
    >
      <MessageCircle size={28} className="text-white" fill="white" />
    </a>
  );
};

// Main App
function App() {
  // Hide Emergent badge
  useHideEmergentBadge();
  
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
}

// App Content (inside LanguageProvider)
function AppContent() {
  return (
    <div className="App">
      <div className="noise-overlay" />
      <NewAddressPopup />
      <Navigation />
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <WhyChooseSection />
      <TeamSection />
      <LocationsSection />
      <ContactSection />
      <FacilityShowcase />
      <Footer />
      <WhatsAppButton />
    </div>
  );
}

export default App;
